import React, { memo, useEffect, useRef, useState } from 'react';
import { ASSET_V } from '../utils/assets';

/**
 * Mars dust video + lightweight CSS nebula. Pauses when tab hidden to save battery.
 */
const VideoBackground = memo(function VideoBackground() {
  const videoRef = useRef(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduced) return undefined;

    const tryPlay = () => {
      video.muted = true;
      video.playsInline = true;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    const onVis = () => {
      if (document.hidden) video.pause();
      else tryPlay();
    };

    video.addEventListener('canplay', tryPlay);
    video.addEventListener('loadeddata', tryPlay);
    document.addEventListener('visibilitychange', onVis);
    document.addEventListener('touchstart', tryPlay, { once: true, passive: true });
    tryPlay();

    return () => {
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('loadeddata', tryPlay);
      document.removeEventListener('visibilitychange', onVis);
      document.removeEventListener('touchstart', tryPlay);
    };
  }, [reduced]);

  return (
    <div
      className={`video-background has-baked-overlay${reduced ? ' is-reduced' : ''}`}
      aria-hidden
      // Dust + vignette + the ::after grade, baked into one source-over layer so
      // the phone stops running two backdrop-reading blends across the whole
      // screen every frame. Passed as a custom property because css-loader tries
      // to resolve absolute url() paths at build time (same trick as --fx-img).
      style={{ '--bg-overlay': `url(/assets/media/bg-overlay.webp?v=${ASSET_V})` }}
    >
      <div className="bg-nebula" />
      <div className="bg-dust" />
      <div className="bg-vignette" />
      {!reduced ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          id="background-video"
          // Decorative plate, not media. Without these iOS wires it into the
          // remote-playback / AirPlay routing machinery and offers it up in the
          // system player, which keeps services alive around a silent loop.
          disableRemotePlayback
          x-webkit-airplay="deny"
          controls={false}
        >
          {/* Versioned: nginx serves media immutable for 7d, and this file now
            carries the colour grade that used to be a CSS filter — a stale copy
            would leave the background flat until the cache expired. */}
        <source src={`/assets/media/bg_video.mp4?v=${ASSET_V}`} type="video/mp4" />
        </video>
      ) : null}
    </div>
  );
});

export default VideoBackground;
